import {
  Ban,
  CirclePlus,
  Eye,
  type LucideIcon,
  Pencil,
  Trash2,
} from "lucide-react";

/**
 * Audit enum constants mirroring the proto definitions
 * These match audit/v1/audit.proto
 */

// API string values (what the backend sends)
export const AUDIT_ACTIONS = {
  CREATED: "AUDIT_ACTION_CREATED",
  UPDATED: "AUDIT_ACTION_UPDATED",
  DELETED: "AUDIT_ACTION_DELETED",
  DISABLED: "AUDIT_ACTION_DISABLED",
  VIEWED: "AUDIT_ACTION_VIEWED",
} as const;

export const AUDIT_SOURCES = {
  API: "AUDIT_SOURCE_API",
  DASHBOARD: "AUDIT_SOURCE_DASHBOARD",
  CLI: "AUDIT_SOURCE_CLI",
  SDK: "AUDIT_SOURCE_SDK",
  SYSTEM: "AUDIT_SOURCE_SYSTEM",
} as const;

export const RESOURCE_TYPES = {
  API_KEY: "RESOURCE_TYPE_API_KEY",
  CUSTOMER: "RESOURCE_TYPE_CUSTOMER",
  INVITATION: "RESOURCE_TYPE_INVITATION",
  ORGANIZATION: "RESOURCE_TYPE_ORGANIZATION",
  PROJECT: "RESOURCE_TYPE_PROJECT",
  PROJECT_MEMBER: "RESOURCE_TYPE_PROJECT_MEMBER",
  USER: "RESOURCE_TYPE_USER",
  WORKER: "RESOURCE_TYPE_WORKER",
  GATEWAY: "RESOURCE_TYPE_GATEWAY",
  PLUGIN: "RESOURCE_TYPE_PLUGIN",
  HOOK: "RESOURCE_TYPE_HOOK",
  MODEL: "RESOURCE_TYPE_MODEL",
  AUTH_PROVIDER: "RESOURCE_TYPE_AUTH_PROVIDER",
  SECRET: "RESOURCE_TYPE_SECRET",
  USER_CONNECTION: "RESOURCE_TYPE_USER_CONNECTION",
  DEPLOYMENT: "RESOURCE_TYPE_DEPLOYMENT",
  SETTING: "RESOURCE_TYPE_SETTING",
  USER_SOURCE: "RESOURCE_TYPE_USER_SOURCE",
  TOOL_EXECUTION: "RESOURCE_TYPE_TOOL_EXECUTION",
} as const;

export type AuditAction = (typeof AUDIT_ACTIONS)[keyof typeof AUDIT_ACTIONS];
export type AuditSource = (typeof AUDIT_SOURCES)[keyof typeof AUDIT_SOURCES];
export type ResourceType = (typeof RESOURCE_TYPES)[keyof typeof RESOURCE_TYPES];

/**
 * Format enum values for display
 */
export function formatAuditAction(action: string): string {
  switch (action) {
    case AUDIT_ACTIONS.CREATED:
      return "created";
    case AUDIT_ACTIONS.UPDATED:
      return "updated";
    case AUDIT_ACTIONS.DELETED:
      return "deleted";
    case AUDIT_ACTIONS.DISABLED:
      return "disabled";
    case AUDIT_ACTIONS.VIEWED:
      return "viewed";
    default:
      return action.replace("AUDIT_ACTION_", "").toLowerCase();
  }
}

export function formatAuditSource(source: string): string {
  switch (source) {
    case AUDIT_SOURCES.API:
      return "API";
    case AUDIT_SOURCES.DASHBOARD:
      return "Dashboard";
    case AUDIT_SOURCES.CLI:
      return "CLI";
    case AUDIT_SOURCES.SDK:
      return "SDK";
    default:
      return source.replace("AUDIT_SOURCE_", "");
  }
}

export function formatResourceType(resourceType: string): string {
  switch (resourceType) {
    case RESOURCE_TYPES.API_KEY:
      return "API Key";
    case RESOURCE_TYPES.CUSTOMER:
      return "Customer";
    case RESOURCE_TYPES.INVITATION:
      return "Invitation";
    case RESOURCE_TYPES.ORGANIZATION:
      return "Organization";
    case RESOURCE_TYPES.PROJECT:
      return "Project";
    case RESOURCE_TYPES.PROJECT_MEMBER:
      return "Project Member";
    case RESOURCE_TYPES.USER:
      return "User";
    case RESOURCE_TYPES.WORKER:
      return "Worker";
    case RESOURCE_TYPES.GATEWAY:
      return "Gateway";
    case RESOURCE_TYPES.PLUGIN:
      return "Plugin";
    case RESOURCE_TYPES.HOOK:
      return "Hook";
    case RESOURCE_TYPES.MODEL:
      return "Model";
    case RESOURCE_TYPES.AUTH_PROVIDER:
      return "Auth Provider";
    case RESOURCE_TYPES.SECRET:
      return "Secret";
    case RESOURCE_TYPES.USER_CONNECTION:
      return "User Connection";
    case RESOURCE_TYPES.DEPLOYMENT:
      return "Deployment";
    case RESOURCE_TYPES.SETTING:
      return "Setting";
    case RESOURCE_TYPES.USER_SOURCE:
      return "User Source";
    case RESOURCE_TYPES.TOOL_EXECUTION:
      return "Tool Execution";
    default:
      return resourceType.replace("RESOURCE_TYPE_", "");
  }
}

/**
 * Get action badge color classes
 */
export function getActionIcon(action: string): LucideIcon {
  switch (action) {
    case AUDIT_ACTIONS.CREATED:
      return CirclePlus;
    case AUDIT_ACTIONS.UPDATED:
      return Pencil;
    case AUDIT_ACTIONS.DELETED:
      return Trash2;
    case AUDIT_ACTIONS.DISABLED:
      return Ban;
    case AUDIT_ACTIONS.VIEWED:
      return Eye;
    default:
      return Pencil;
  }
}

/**
 * Get the icon-friendly color class for an action (foreground only, no background)
 */
export function getActionIconColor(action: string): string {
  switch (action) {
    case AUDIT_ACTIONS.CREATED:
      return "text-green-600 dark:text-green-400";
    case AUDIT_ACTIONS.UPDATED:
      return "text-blue-600 dark:text-blue-400";
    case AUDIT_ACTIONS.DELETED:
      return "text-red-600 dark:text-red-400";
    case AUDIT_ACTIONS.DISABLED:
      return "text-yellow-600 dark:text-yellow-400";
    case AUDIT_ACTIONS.VIEWED:
      return "text-purple-600 dark:text-purple-400";
    default:
      return "text-muted-foreground";
  }
}
